import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function ApartmentAdminForm({
  form,
  setForm,
  propertyManagers,
  saving,
  onCancel,
  onSave,
  saveLabelKey = 'admin.apt.save',
  cancelTo,
}) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-[#2B2B2B]/10 rounded-lg p-4 sm:p-6 space-y-6 shadow-sm">
      <section>
        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold mb-3">{t('admin.apt.sectionBasic')}</p>
        <div className="space-y-3">
          <div><Label className="text-xs">{t('admin.apt.fieldTitle')}</Label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="mt-1" data-testid="apt-title-input" /></div>
          <div><Label className="text-xs">{t('admin.apt.fieldAddress')}</Label><Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} className="mt-1" data-testid="apt-address-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">{t('admin.apt.fieldNeighborhood')}</Label><Input value={form.neighborhood} onChange={(e) => setForm((p) => ({ ...p, neighborhood: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">{t('admin.apt.fieldRent')}</Label><Input type="number" value={form.rent} onChange={(e) => setForm((p) => ({ ...p, rent: e.target.value }))} className="mt-1" data-testid="apt-rent-input" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">{t('admin.apt.fieldRooms')}</Label><Input type="number" value={form.rooms} onChange={(e) => setForm((p) => ({ ...p, rooms: e.target.value }))} className="mt-1" data-testid="apt-rooms-input" /></div>
            <div><Label className="text-xs">{t('admin.apt.fieldSize')}</Label><Input type="number" value={form.size} onChange={(e) => setForm((p) => ({ ...p, size: e.target.value }))} className="mt-1" data-testid="apt-size-input" /></div>
          </div>
        </div>
      </section>

      <section>
        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold mb-3">{t('admin.apt.sectionAssignment')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t('admin.apt.fieldManager')}</Label>
            <Select value={form.property_manager_id || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, property_manager_id: v === 'none' ? '' : v, source: v === 'none' ? p.source : 'manager' }))}>
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="apt-pm-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs">{t('admin.apt.noManager')}</SelectItem>
                {propertyManagers.filter((pm) => !pm.archived).map((pm) => (
                  <SelectItem key={pm.id} value={pm.id} className="text-xs">{pm.first_name} {pm.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('admin.apt.fieldSource')}</Label>
            <Select value={form.source || 'auto'} onValueChange={(v) => setForm((p) => ({ ...p, source: v === 'auto' ? '' : v }))}>
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="apt-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto" className="text-xs">{t('admin.apt.sourceAuto')}</SelectItem>
                <SelectItem value="manager" className="text-xs">{t('admin.apt.sourceManager')}</SelectItem>
                <SelectItem value="portal" className="text-xs">{t('admin.apt.sourcePortal')}</SelectItem>
                <SelectItem value="direct_owner" className="text-xs">{t('admin.apt.sourceDirect')}</SelectItem>
                <SelectItem value="other" className="text-xs">{t('admin.apt.sourceOther')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section>
        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold mb-3">{t('admin.apt.sectionListing')}</p>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Label className="text-xs">{t('admin.apt.fieldFurnished')}</Label>
            <Switch checked={form.furnished} onCheckedChange={(v) => setForm((p) => ({ ...p, furnished: v }))} data-testid="apt-furnished-switch" />
          </div>
          <div><Label className="text-xs">{t('admin.apt.fieldDescription')}</Label><Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="mt-1 min-h-[80px]" data-testid="apt-description-input" /></div>
          <div><Label className="text-xs">{t('admin.apt.fieldExternalUrl')}</Label><Input value={form.external_url} onChange={(e) => setForm((p) => ({ ...p, external_url: e.target.value }))} className="mt-1" placeholder="https://www.homegate.ch/..." data-testid="apt-external-url-input" /></div>
        </div>
      </section>

      <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2 border-t border-[#2B2B2B]/10">
        <Button variant="ghost" className="w-full sm:w-auto" onClick={onCancel} data-testid="apt-form-cancel-btn">{t('common.cancel')}</Button>
        <Button className="w-full sm:w-auto bg-[#0B3D2E] text-white" onClick={onSave} disabled={saving} data-testid="apt-form-save-btn">
          {saving ? t('common.loading') : t(saveLabelKey)}
        </Button>
      </div>
    </div>
  );
}
